import { createStore } from 'vuex'
import { BACKGROUND_IDS, findBackgroundOption } from '../constants/backgrounds'

const FAVORITES_KEY = 'rio-dashboard-favorites'
const COMPARE_KEY = 'rio-dashboard-compare'
const SETTINGS_KEY = 'rio-dashboard-settings'
const DEFAULT_NOTIFICATION_TIMEOUT = 4500
const FULLSCREEN_LOADER_DELAY = 1800
let nextNotificationId = 1

function readJson(key, fallbackValue) {
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : fallbackValue
    } catch (error) {
        return fallbackValue
    }
}

function writeJson(key, value) {
    localStorage.setItem(key, JSON.stringify(value))
}

function delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function markReloadLoader() {
    try {
        sessionStorage.setItem('rio-dashboard-show-reload-loader', '1')
    } catch (error) {
        console.error('Reload loader flag failed', error)
    }
}

function getCharacterId(profile = {}) {
    return `${profile.region || ''}:${profile.realm || ''}:${profile.name || ''}`.toLowerCase()
}

function normalizeCompareEntry(entry) {
    if (!entry?.profile) return null

    return {
        ...entry,
        id: entry.id || getCharacterId(entry.profile),
    }
}

function showNotification(commit, payload = {}) {
    const notification = {
        id: nextNotificationId++,
        type: ['success', 'warning', 'danger'].includes(payload.type) ? payload.type : 'warning',
        title: payload.title || 'Notification',
        description: payload.description || ''
    }

    commit('pushNotification', notification)

    if (payload.timeout !== 0) {
        window.setTimeout(
            () => commit('removeNotification', notification.id),
            payload.timeout || DEFAULT_NOTIFICATION_TIMEOUT
        )
    }
}

export default createStore({
    state: {
        theme: 'claude',
        themeMode: 'light',
        appBackground: 'none',
        favorites: [],
        compareList: [],
        deferredPrompt: null,
        canInstall: false,
        swRegistration: null,
        updateAvailable: false,
        activeSeasonLabel: 'Current Raider.io season',
        notifications: [],
        confirmation: null,
        globalLoadingCount: 0,
        globalLoadingMessage: '',
        fullScreenLoading: false,
        fullScreenLoadingMessage: ''
    },
    getters: {
        getTheme(state) {
            return state.theme
        },
        getThemeMode(state) {
            return state.themeMode
        },
        getFavorites(state) {
            return state.favorites
        },
        getAppBackground(state) {
            return state.appBackground
        },
        getCompareList(state) {
            return state.compareList
        },
        getCompareIds(state) {
            return state.compareList.map((item) => item.id)
        },
        getCanInstall(state) {
            return state.canInstall
        },
        getUpdateAvailable(state) {
            return state.updateAvailable
        },
        getActiveSeasonLabel(state) {
            return state.activeSeasonLabel
        },
        getNotifications(state) {
            return state.notifications
        },
        getConfirmation(state) {
            return state.confirmation
        },
        getGlobalLoading(state) {
            return state.globalLoadingCount > 0
        },
        getGlobalLoadingMessage(state) {
            return state.globalLoadingMessage || 'Loading data...'
        },
        getFullScreenLoading(state) {
            return state.fullScreenLoading
        },
        getFullScreenLoadingMessage(state) {
            return state.fullScreenLoadingMessage || 'Updating dashboard...'
        }
    },
    mutations: {
        setTheme(state, mode = 'light') {
            state.theme = 'claude'
            state.themeMode = mode === 'dark' ? 'dark' : 'light'

            document.documentElement.classList.add('theme-claude')
            document.documentElement.classList.toggle('dark', state.themeMode === 'dark')
            document.body.setAttribute('data-theme', 'claude')

            try {
                localStorage.setItem('themeMode', state.themeMode)
                localStorage.setItem('themeVariant', 'claude')
            } catch (error) {
                console.error('Theme settings save failed', error)
            }
        },
        setAppBackground(state, backgroundId = 'none') {
            state.appBackground = findBackgroundOption(backgroundId).id
        },
        setFavorites(state, favorites) {
            state.favorites = Array.isArray(favorites) ? favorites : []
            writeJson(FAVORITES_KEY, state.favorites)
        },
        setCompareList(state, compareList) {
            state.compareList = Array.isArray(compareList)
                ? compareList.map(normalizeCompareEntry).filter(Boolean)
                : []
            writeJson(COMPARE_KEY, state.compareList)
        },
        setInstallPrompt(state, prompt) {
            state.deferredPrompt = prompt
            state.canInstall = Boolean(prompt)
        },
        setServiceWorkerRegistration(state, registration) {
            state.swRegistration = registration
        },
        setUpdateAvailable(state, value) {
            state.updateAvailable = Boolean(value)
        },
        setSeasonLabel(state, label) {
            state.activeSeasonLabel = label || 'Current Raider.io season'
            writeJson(SETTINGS_KEY, {
                theme: 'claude',
                themeMode: state.themeMode,
                activeSeasonLabel: state.activeSeasonLabel,
                appBackground: state.appBackground
            })
        },
        persistSettings(state) {
            writeJson(SETTINGS_KEY, {
                theme: 'claude',
                themeMode: state.themeMode,
                activeSeasonLabel: state.activeSeasonLabel,
                appBackground: state.appBackground
            })
        },
        pushNotification(state, notification) {
            state.notifications = [notification, ...state.notifications].slice(0, 4)
        },
        removeNotification(state, id) {
            state.notifications = state.notifications.filter((notification) => notification.id !== id)
        },
        setConfirmation(state, confirmation) {
            state.confirmation = confirmation
        },
        clearConfirmation(state) {
            state.confirmation = null
        },
        startGlobalLoading(state, message) {
            state.globalLoadingCount += 1
            if (message) {
                state.globalLoadingMessage = message
            }
        },
        finishGlobalLoading(state) {
            state.globalLoadingCount = Math.max(0, state.globalLoadingCount - 1)
            if (state.globalLoadingCount === 0) {
                state.globalLoadingMessage = ''
            }
        },
        setFullScreenLoading(state, payload = {}) {
            state.fullScreenLoading = Boolean(payload.active)
            state.fullScreenLoadingMessage = payload.message || ''
        }
    },
    actions: {
        initialize({ commit }) {
            const savedSettings = readJson(SETTINGS_KEY, {})
            const savedFavorites = readJson(FAVORITES_KEY, [])
            const savedCompare = readJson(COMPARE_KEY, [])

            const storedMode = localStorage.getItem('themeMode')
            const systemMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
            commit('setTheme', savedSettings.themeMode || storedMode || systemMode)
            commit('setAppBackground', savedSettings.appBackground)
            commit('setSeasonLabel', savedSettings.activeSeasonLabel || 'Current Raider.io season')
            commit('setFavorites', savedFavorites)
            commit('setCompareList', savedCompare)

            window.addEventListener('beforeinstallprompt', (event) => {
                event.preventDefault()
                commit('setInstallPrompt', event)
            })

            window.addEventListener('appinstalled', () => {
                commit('setInstallPrompt', null)
            })
        },
        setSeasonLabel({ commit }, label) {
            commit('setSeasonLabel', label)
        },
        setThemeMode({ commit }, mode) {
            commit('setTheme', mode)
            commit('persistSettings')
        },
        setAppBackground({ commit }, backgroundId) {
            commit('setAppBackground', BACKGROUND_IDS.includes(backgroundId) ? backgroundId : 'none')
            commit('persistSettings')
        },
        toggleTheme({ state, commit }) {
            commit('setTheme', state.themeMode === 'dark' ? 'light' : 'dark')
            commit('persistSettings')
            showNotification(commit, {
                type: 'warning',
                title: 'Theme updated',
                description: `Switched to ${state.themeMode} mode.`
            })
        },
        addFavorite({ state, commit }, { character, source = 'manual' }) {
            const normalized = {
                id: getCharacterId(character),
                source,
                addedAt: new Date().toISOString(),
                profile: character
            }
            const favorites = [...state.favorites]
            const existingIndex = favorites.findIndex((item) => item.id === normalized.id)

            if (existingIndex >= 0) {
                favorites.splice(existingIndex, 1, normalized)
            } else {
                favorites.unshift(normalized)
            }

            commit('setFavorites', favorites)
            showNotification(commit, {
                type: 'success',
                title: 'Favorite saved',
                description: `${character.name} is now in favorites.`
            })
        },
        removeFavorite({ state, commit }, id) {
            commit('setFavorites', state.favorites.filter((item) => item.id !== id))
            commit('setCompareList', state.compareList.filter((item) => item.id !== id))
            showNotification(commit, {
                type: 'danger',
                title: 'Favorite removed',
                description: 'The character was removed from saved profiles.'
            })
        },
        addToCompare({ state, commit }, favorite) {
            const normalized = normalizeCompareEntry(favorite)
            if (!normalized?.id) {
                showNotification(commit, {
                    type: 'danger',
                    title: 'Compare failed',
                    description: 'This character cannot be added to compare.'
                })
                return
            }

            const id = normalized.id
            if (state.compareList.some((item) => item.id === id)) {
                showNotification(commit, {
                    type: 'warning',
                    title: 'Already in compare',
                    description: `${favorite.profile.name} is already in the compare list.`
                })
                return
            }

            const compareList = [...state.compareList]
            if (compareList.length >= 6) compareList.shift()
            compareList.push(normalized)
            commit('setCompareList', compareList)
            showNotification(commit, {
                type: 'success',
                title: 'Added to compare',
                description: `${favorite.profile.name} is ready for comparison.`
            })
        },
        removeFromCompare({ state, commit }, id) {
            const index = state.compareList.findIndex((item) => item.id === id)
            if (index < 0) return

            const compareList = [...state.compareList]
            compareList.splice(index, 1)
            commit('setCompareList', compareList)
            showNotification(commit, {
                type: 'danger',
                title: 'Compare updated',
                description: 'The character was removed from compare.'
            })
        },
        clearCompare({ commit }) {
            commit('setCompareList', [])
            showNotification(commit, {
                type: 'danger',
                title: 'Compare cleared',
                description: 'The compare list is empty now.'
            })
        },
        clearLocalData({ commit }) {
            commit('setFavorites', [])
            commit('setCompareList', [])
            localStorage.removeItem(FAVORITES_KEY)
            localStorage.removeItem(COMPARE_KEY)
            showNotification(commit, {
                type: 'danger',
                title: 'Local data cleared',
                description: 'Favorites and compare data were removed from this device.'
            })
        },
        async promptInstall({ state, commit }) {
            if (!state.deferredPrompt) {
                showNotification(commit, {
                    type: 'warning',
                    title: 'Install unavailable',
                    description: 'The app cannot be installed from this browser state yet.'
                })
                return
            }

            state.deferredPrompt.prompt()
            await state.deferredPrompt.userChoice
            commit('setInstallPrompt', null)
        },
        async registerServiceWorker({ commit }) {
            if (!('serviceWorker' in navigator)) return

            try {
                const registration = await navigator.serviceWorker.register('/sw.js')
                commit('setServiceWorkerRegistration', registration)

                if (registration.waiting) {
                    commit('setUpdateAvailable', true)
                }

                registration.addEventListener('updatefound', () => {
                    const worker = registration.installing
                    if (!worker) return

                    worker.addEventListener('statechange', () => {
                        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                            commit('setUpdateAvailable', true)
                        }
                    })
                })

                navigator.serviceWorker.addEventListener('controllerchange', () => {
                    commit('setUpdateAvailable', false)
                })
            } catch (error) {
                console.error('Service worker registration failed', error)
            }
        },
        async checkForUpdates({ state, commit }) {
            if (!state.swRegistration) {
                showNotification(commit, {
                    type: 'warning',
                    title: 'Updates unavailable',
                    description: 'Service worker updates are only available in the installed production app.'
                })
                return
            }

            commit('startGlobalLoading', 'Checking for updates...')
            try {
                await state.swRegistration.update()
                commit('setUpdateAvailable', Boolean(state.swRegistration.waiting))
                showNotification(commit, {
                    type: state.swRegistration.waiting ? 'success' : 'warning',
                    title: state.swRegistration.waiting ? 'Update available' : 'No updates found',
                    description: state.swRegistration.waiting ? 'A new app version is ready to apply.' : 'You are already on the latest app version.'
                })
            } finally {
                commit('finishGlobalLoading')
            }
        },
        async refreshApplication({ state, commit }) {
            commit('startGlobalLoading', 'Updating application...')
            commit('setFullScreenLoading', { active: true, message: 'Updating dashboard...' })
            markReloadLoader()

            const reloadApp = () => {
                window.location.reload()
            }

            if (!state.swRegistration || !('serviceWorker' in navigator)) {
                await delay(FULLSCREEN_LOADER_DELAY)
                reloadApp()
                return
            }

            await delay(FULLSCREEN_LOADER_DELAY)

            let reloaded = false
            const reloadOnce = () => {
                if (reloaded) return
                reloaded = true
                reloadApp()
            }

            const handleControllerChange = () => {
                navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
                reloadOnce()
            }

            navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)

            try {
                await state.swRegistration.update()

                if (state.swRegistration.waiting) {
                    state.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
                    window.setTimeout(reloadOnce, 900)
                    return
                }

                const worker = state.swRegistration.installing
                if (worker) {
                    worker.addEventListener('statechange', () => {
                        if (worker.state === 'installed' || worker.state === 'activated') {
                            window.setTimeout(reloadOnce, 250)
                        }
                    })

                    window.setTimeout(reloadOnce, 1800)
                    return
                }

                window.setTimeout(reloadOnce, 150)
            } catch (error) {
                navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
                showNotification(commit, {
                    type: 'warning',
                    title: 'Update check failed',
                    description: 'Reloading the application with the current local data.'
                })
                window.setTimeout(reloadOnce, 180)
            }
        },
        applyUpdate({ state, commit }) {
            if (!state.swRegistration || !state.swRegistration.waiting) return

            commit('startGlobalLoading', 'Applying update...')
            commit('setFullScreenLoading', { active: true, message: 'Applying update...' })
            markReloadLoader()
            window.setTimeout(() => {
                state.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
                window.setTimeout(() => window.location.reload(), 250)
            }, FULLSCREEN_LOADER_DELAY)
        },
        startGlobalLoading({ commit }, message) {
            commit('startGlobalLoading', message)
        },
        finishGlobalLoading({ commit }) {
            commit('finishGlobalLoading')
        },
        showFullScreenLoader({ commit }, payload = {}) {
            commit('setFullScreenLoading', {
                active: true,
                message: payload.message,
            })

            if (payload.duration !== 0) {
                window.setTimeout(
                    () => commit('setFullScreenLoading', { active: false }),
                    payload.duration || FULLSCREEN_LOADER_DELAY
                )
            }
        },
        hideFullScreenLoader({ commit }) {
            commit('setFullScreenLoading', { active: false })
        },
        notify({ commit }, payload) {
            showNotification(commit, payload)
        },
        dismissNotification({ commit }, id) {
            commit('removeNotification', id)
        },
        requestConfirmation({ commit }, payload) {
            commit('setConfirmation', {
                title: payload?.title || 'Confirm action',
                message: payload?.message || 'This action cannot be undone.',
                confirmLabel: payload?.confirmLabel || 'Delete',
                cancelLabel: payload?.cancelLabel || 'Cancel',
                action: payload?.action || null,
                payload: payload?.payload
            })
        },
        confirmAction({ state, commit, dispatch }) {
            const confirmation = state.confirmation
            commit('clearConfirmation')
            if (confirmation?.action) {
                dispatch(confirmation.action, confirmation.payload)
            }
        },
        cancelConfirmation({ commit }) {
            commit('clearConfirmation')
        }
    }
})
